import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { rottaEsistente, selezionaCampoNome } from './nomeGiaPresente';

// DBT-157 — finestra «nome già presente» (Regola 14: mai l'alert col JSON).
// `info` è il risultato di `nomeGiaPresenteDaErrore`: titolo e testo arrivano dal
// backend (CMS di AIstotele). Due azioni:
//  · «Modifica il nome» chiude la finestra e seleziona il campo nome del modulo,
//    che resta aperto con i dati già inseriti;
//  · «Apri il <tipo> esistente» porta al record già presente. Non c'è quando a
//    bloccare è una cartella rimasta con quel nome e non un record.
// `onApriEsistente` (opzionale): chi sa aprire il record senza cambiare pagina
// (es. la scheda cliente nella lista Clienti) lo fa da sé.
// `onChiudiModulo` (opzionale): chi apre il modale da dentro un altro modale lo
// chiude prima di portare al record esistente.
export default function NomeGiaPresenteModal({ info, onClose, onApriEsistente, onChiudiModulo }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!info) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [info, onClose]);

  if (!info) return null;

  const modifica = () => {
    onClose();
    // Dopo la chiusura, quando il modulo è di nuovo in primo piano.
    setTimeout(() => selezionaCampoNome(), 0);
  };
  const apri = () => {
    onClose();
    if (onChiudiModulo) onChiudiModulo();
    if (onApriEsistente) onApriEsistente(info.esistenteId, info.tipo);
    else navigate(rottaEsistente(info.tipo, info.esistenteId));
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1100 }} />
      <div
        role="dialog" aria-modal="true" aria-labelledby="nome-gia-presente-titolo"
        data-testid="nome-gia-presente-modal"
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 'min(92vw, 460px)', maxHeight: '85vh', overflowY: 'auto', zIndex: 1101,
          background: 'var(--ds-bg)', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-card)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)', padding: 24, display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <h2 id="nome-gia-presente-titolo" style={{ margin: 0, fontSize: 17, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)', lineHeight: 1.3 }}>
            {info.titolo}
          </h2>
          <button onClick={onClose} aria-label="Chiudi" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', padding: 0, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
            <X size={20} strokeWidth={2} />
          </button>
        </div>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 400, color: 'var(--ds-text)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
          {info.testo}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          <button onClick={modifica} style={btnPrimario}>Modifica il nome</button>
          {info.esistenteId && (
            <button onClick={apri} style={btnSecondario}>{'Apri il ' + info.tipo + ' esistente'}</button>
          )}
        </div>
      </div>
    </>
  );
}

const btnBase = {
  minHeight: 44, padding: '10px 18px', borderRadius: 'var(--radius-base)', fontSize: 14,
  fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', width: '100%',
};
const btnPrimario = { ...btnBase, border: 'none', background: 'var(--brand)', color: '#fff' };
const btnSecondario = { ...btnBase, border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ink)' };
